import classNames from "classnames";
import { useRef } from "react";
import { NodeStates, DivNode } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { BuilderComponentProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./style.scss";
import Loader from "../../assets/VismaLoader.gif";

const BuilderDiv: React.FC<BuilderComponentProps> = ({
    node,
    children,
    actionCallbacks,
    dynamicData,
    builderProps,
    disabled,
    selected,
    isLoading
}: BuilderComponentProps) => {
    const dNode = node as DivNode;
    const { id, attr, stateManager, backgroundImage, doLoading } = dNode;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();
    const div = useRef<HTMLDivElement>(null);

    const builderDivClass = classNames("builder-div", {
        [`${attr?.className}`]: !!attr?.className
    });

    const getLoadingElement = () => {
        if (dNode.id) {
            const currentDiv = document.getElementById(dNode.id);
            if (currentDiv) {
                return (
                    <div
                        style={{
                            width: currentDiv.clientWidth,
                            height: currentDiv.clientHeight,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                        className="builder-div-loading"
                    >
                        <img src={Loader} style={{ width: "150px", height: "150px" }} />
                    </div>
                );
            }
        }
        return null;
    };

    const backgroundImageStyle: React.CSSProperties =
        node.dynamicData || backgroundImage
            ? {
                  backgroundImage: `url("${node.dynamicData ? dynamicData : backgroundImage}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat"
              }
            : {};

    if (builderProps) {
        const {
            hovered,
            isContainer,
            nodeState,
            extraChild,
            selectedMenu,
            selectCallback,
            outlineCallback,
            onDragOverCapture,
            onDragStart,
            onDragEnd
        } = builderProps;

        return (
            <div
                {...attr}
                ref={div}
                className={`${builderDivClass}`}
                style={getCompoundStyle({
                    node,
                    isContainer: isContainer,
                    otherStyle: backgroundImageStyle,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragOverCapture={e => onDragOverCapture?.(e, id, div.current as HTMLDivElement)}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {children}
                {!hovered &&
                    (!div.current?.clientWidth || div.current?.clientWidth >= 30) &&
                    (!div.current?.clientHeight || div.current?.clientHeight >= 30) &&
                    extraChild}
                {selectedMenu}
            </div>
        );
    } else {
        const { style, ...rest } = attr || {};

        const extraStates = (
            disabled || selected ? [selected && NodeStates.SELECTED, disabled && NodeStates.DISABLED].filter(Boolean) : undefined
        ) as NodeStates[];

        const divStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node, otherStyle: backgroundImageStyle }),
            stateManager,
            paths: ["attr_style"],
            ...(extraStates ? { extraStates } : {})
        });

        return (
            <div ref={setElement} style={divStyle} id={id} {...rest} {...actionCallbacks} className={builderDivClass}>
                {isLoading && doLoading ? getLoadingElement() : children}
            </div>
        );
    }
};

export default BuilderDiv;
